import React from 'react';
import { observer } from 'mobx-react';
import { useIntl } from 'react-intl';
import { MeetingLinkRole } from '../../../../../../../api/meeting-links.dao';
import { CommonPopupBodyFooterLayout } from '../../../../../../global/common-popup/common-popup-body-footer-layout';
import { FormField } from '../../../../../../global/form/form-field/form-field';
import { FormFieldInput } from '../../../../../../global/form/form-field-input/form-field-input';
import { DefaultCloseSubWindowButton } from '../../../../../../global/sub-window/common/common-close-sub-window-buttons';
import Text from '../../../../../../global/text/text';
import { cancelText, submitText } from '../../../../../../../constants/common-labels.constants';
import { ConnectionDropdownPresentation } from '../../../../shared/_connection-dropdown/connection-dropdown.presentation';
import { ManageMeetingLinkModalViewModel } from './manage-meeting-link-modal.viewmodel';
import { EnablementStatus } from '../../../../../../../constants/common.constants';
import {
    CONNECTION_COLUMN_TEXT,
    CREATE_PANELIST_LINK_TOGGLE_INFO_TEXT,
    CREATE_PANELIST_LINK_TOGGLE_TEXT,
    MEETING_LINK_LABEL_TEXT,
    MEETING_LINK_ROLE_TEXT,
} from '../meeting-links.texts';

export type ManageMeetingLinkModalPresentationProps = {
    vm: ManageMeetingLinkModalViewModel;
};

export const ManageMeetingLinkModalPresentation = observer(({ vm }: ManageMeetingLinkModalPresentationProps): JSX.Element => {
    const intl = useIntl();

    return (
        <CommonPopupBodyFooterLayout
            body={
                <form className='oe-form add-meeting-link-form'>
                    <div className='oe-form__body'>
                        <FormField label={intl.formatMessage(MEETING_LINK_LABEL_TEXT)}>
                            <input
                                className='oe-form-field__control'
                                value={vm.label}
                                onChange={e => vm.setLabel(e.target.value)}
                            />
                            {vm.labelErrorMessage &&
                                <span className='oe-form-field__error'>{vm.labelErrorMessage}</span>
                            }
                        </FormField>
                        <FormField label={intl.formatMessage(MEETING_LINK_ROLE_TEXT)}>
                            <select
                                className='oe-form-field__control'
                                value={vm.role ?? ''}
                                onChange={e => vm.setRole((e.target.value || null) as MeetingLinkRole | null)}
                            >
                                <option value=''>{ }</option>
                                <option value={MeetingLinkRole.Presenter}>{MeetingLinkRole.Presenter}</option>
                                <option value={MeetingLinkRole.Host}>{MeetingLinkRole.Host}</option>
                                <option value={MeetingLinkRole.Support}>{MeetingLinkRole.Support}</option>
                            </select>
                        </FormField>
                        {vm.showConnectionField &&
                            <>
                                <FormField label={intl.formatMessage(CONNECTION_COLUMN_TEXT)}>
                                    <ConnectionDropdownPresentation
                                        cssClass='add-meeting-link-form__connection-dropdown'
                                        meetingConnections={vm.connections}
                                        connectionGroups={vm.connectionGroupsList}
                                        assignedConnectionId={vm.assignedConnectionId}
                                        derivedConnection={vm.derivedConnection}
                                        isLabelVisibleCondition={true}
                                        onChange={vm.setConnectionId} />
                                </FormField>
                                <FormFieldInput.Toggle
                                    className='m-top-10px add-meeting-link-form__panelist-toggle'
                                    isSelected={vm.createPanelistLinkEnabled}
                                    toggle={vm.togglePanelistLinkCreationEnabled}
                                    text={<Text
                                        {...CREATE_PANELIST_LINK_TOGGLE_TEXT}
                                        values={{
                                            suffix: vm.createPanelistLinkEnabled
                                                ? EnablementStatus.ENABLED_UPPERCASE
                                                : EnablementStatus.DISABLED_UPPERCASE,
                                        }} />}
                                    popoverText={intl.formatMessage(CREATE_PANELIST_LINK_TOGGLE_INFO_TEXT)}
                                />
                            </>
                        }
                    </div>
                </form>
            }
            footer={
                <div className='buttons-block buttons-block--from-right'>
                    <DefaultCloseSubWindowButton
                        text={submitText(vm.isEditMode)}
                        modifier={['fat', 'confirm-safe']}
                        onClick={vm.submit}
                        disabled={vm.saving}
                    />
                    <DefaultCloseSubWindowButton text={cancelText()} />
                </div>
            }
        />
    );
});
